import { useQuery } from '@tanstack/react-query';
import { AI_CAPABILITIES, AI_CAPABILITY_LABELS, type AiCapability } from '@lixionary-jira/domain';
import { Copy } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router';
import { api, useAction } from '../../api/hooks';
import { useIsAdmin } from '../../api/session';
import type { AiServiceDetail } from '../../api/types';
import { useCrumbs } from '../../layout/crumbs';
import { Badge, StateBadge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { Alert, ErrorAlert, PageUnavailable, QueryView } from '../../ui/feedback';
import { Checkbox, Field, Input, Select } from '../../ui/forms';
import { ConfirmDialog, Modal } from '../../ui/Modal';
import { ActorName, Card, KeyValue, PageHeader, usePageTitle } from '../../ui/misc';
import { Timestamp } from '../../ui/Timestamp';

/** IDN-06, MCP-01–05: credentials (shown once), per-project grants, and MCP connection guidance. */
export function AiServiceDetailPage() {
  const { serviceId } = useParams();
  const admin = useIsAdmin();
  const q = useQuery({
    queryKey: ['ai-service', serviceId],
    queryFn: () => api.get<AiServiceDetail>(`/admin/ai-services/${serviceId}`),
    enabled: admin,
  });
  usePageTitle(q.data ? `${q.data.name} · AI service` : null);
  useCrumbs([{ label: 'AI services', to: '/admin/ai-services' }, { label: q.data?.name ?? '' }]);
  const [issuing, setIssuing] = useState(false);
  const [revoking, setRevoking] = useState<string | null>(null);
  const [toggle, setToggle] = useState(false);
  const inv = [['ai-service', serviceId], ['ai-services']];
  const revoke = useAction(
    (cid: string) => api.post(`/admin/ai-services/${serviceId}/credentials/${cid}/revoke`),
    { success: 'Credential revoked', invalidate: inv, onSuccess: () => setRevoking(null) },
  );
  const setActive = useAction(
    (active: boolean) =>
      api.patch(`/admin/ai-services/${serviceId}`, { active, expectedVersion: q.data!.version }),
    { success: 'Service updated', invalidate: inv, onSuccess: () => setToggle(false) },
  );
  if (!admin) return <PageUnavailable />;
  return (
    <div className="page">
      <QueryView query={q}>
        {(s) => (
          <>
            <PageHeader
              title={s.name}
              subtitle={
                s.owner ? (
                  <>
                    Personal agent of <ActorName actor={s.owner} />. It mirrors their project access and
                    connects by browser sign-in.
                  </>
                ) : (
                  s.description || 'AI service identity'
                )
              }
              actions={
                <>
                  {s.active ? <Badge tone="success">Active</Badge> : <Badge>Inactive</Badge>}
                  <Button
                    variant={s.active ? 'danger-outline' : 'secondary'}
                    size="sm"
                    onClick={() => setToggle(true)}
                  >
                    {s.active ? 'Deactivate' : 'Activate'}
                  </Button>
                </>
              }
            />
            <div className="split">
              <div className="stack">
                <Card
                  title="Credentials"
                  subtitle="Each credential is individually identifiable, expiring and revocable. Secrets are shown once."
                  action={
                    <Button size="sm" onClick={() => setIssuing(true)} disabled={!s.active}>
                      Issue credential
                    </Button>
                  }
                >
                  {s.credentials.length === 0 ? (
                    <span className="small muted">No credentials issued.</span>
                  ) : (
                    <div className="table-wrap">
                      <table className="table">
                        <thead>
                          <tr>
                            <th>Label</th>
                            <th>Client ID</th>
                            <th>State</th>
                            <th>Expires</th>
                            <th>Last used</th>
                            <th>Issued by</th>
                            <th />
                          </tr>
                        </thead>
                        <tbody>
                          {s.credentials.map((c) => (
                            <tr key={c.id}>
                              <td className="ink">
                                {c.label}
                                {c.kind === 'oauth_grant' && (
                                  <span className="small muted"> · browser sign-in</span>
                                )}
                              </td>
                              <td className="mono small">{c.clientId}</td>
                              <td>
                                <StateBadge state={c.state} />
                              </td>
                              <td className="small">
                                <Timestamp value={c.expiresAt} dateOnly />
                              </td>
                              <td className="small">
                                <Timestamp value={c.lastUsedAt} relative />
                              </td>
                              <td className="small">
                                <ActorName actor={c.createdBy} />
                              </td>
                              <td>
                                {c.state === 'active' && (
                                  <Button size="sm" variant="ghost" onClick={() => setRevoking(c.id)}>
                                    Revoke
                                  </Button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>
                {!s.owner && <GrantsCard service={s} />}
              </div>
              <Card title="MCP connection" tight>
                <KeyValue
                  items={[
                    [
                      'Endpoint',
                      <span key="e" className="mono small">
                        {s.mcp.endpoint}
                      </span>,
                    ],
                    ['Transport', s.mcp.transport],
                    [
                      'Token endpoint',
                      <span key="t" className="mono small">
                        {s.mcp.tokenEndpoint}
                      </span>,
                    ],
                    [
                      'Resource metadata',
                      <span key="r" className="mono small">
                        {s.mcp.resourceMetadata}
                      </span>,
                    ],
                  ]}
                />
                <p className="small muted">
                  People connect their own agents by browser sign-in (OAuth authorization code). For a
                  headless agent, exchange the client ID and secret for a short-lived access token with the
                  OAuth client-credentials grant, then send it as a bearer token. Tokens never carry approval
                  authority. See docs/mcp-clients.md for tested clients.
                </p>
              </Card>
            </div>
            {issuing && <IssueCredential serviceId={s.id} onClose={() => setIssuing(false)} />}
            {revoking && (
              <ConfirmDialog
                title="Revoke credential?"
                confirmLabel="Yes, revoke"
                cancelLabel="Keep credential"
                variant="danger"
                onClose={() => setRevoking(null)}
                onConfirm={() => revoke.mutate(revoking)}
                pending={revoke.isPending}
                error={revoke.error}
              >
                <p>
                  Requests using this credential or its access tokens fail immediately. Active claims held by
                  this service will expire and need admin recovery.
                </p>
              </ConfirmDialog>
            )}
            {toggle && (
              <ConfirmDialog
                title={s.active ? `Deactivate ${s.name}?` : `Activate ${s.name}?`}
                confirmLabel={s.active ? 'Yes, deactivate' : 'Activate'}
                variant={s.active ? 'danger' : 'primary'}
                onClose={() => setToggle(false)}
                onConfirm={() => setActive.mutate(!s.active)}
                pending={setActive.isPending}
                error={setActive.error}
              >
                <p>
                  {s.active
                    ? 'All access tokens are revoked and the service can no longer read or write.'
                    : 'Existing unexpired credentials work again.'}
                </p>
              </ConfirmDialog>
            )}
          </>
        )}
      </QueryView>
    </div>
  );
}

function GrantsCard({ service }: { service: AiServiceDetail }) {
  const [projectId, setProjectId] = useState(service.projects[0]?.id ?? '');
  const project = service.projects.find((p) => p.id === projectId);
  const [caps, setCaps] = useState<AiCapability[] | null>(null);
  const current = caps ?? project?.capabilities ?? [];
  const save = useAction(
    () => api.put(`/admin/ai-services/${service.id}/grants`, { projectId, capabilities: current }),
    {
      success: 'Grants saved',
      invalidate: [['ai-service', service.id], ['ai-services']],
      onSuccess: () => setCaps(null),
    },
  );
  return (
    <Card
      title="Project write grants"
      subtitle="Reads are global. No grant permits approvals, role changes, context edits, scope rewrites, overrides or claim recovery."
    >
      <Field label="Project">
        {(p) => (
          <Select
            {...p}
            value={projectId}
            onChange={(e) => {
              setProjectId(e.target.value);
              setCaps(null);
            }}
          >
            {service.projects.map((pr) => (
              <option key={pr.id} value={pr.id}>
                {pr.key} · {pr.name} ({pr.capabilities.length} granted)
              </option>
            ))}
          </Select>
        )}
      </Field>
      {project && (
        <>
          {project.archived && (
            <Alert tone="info">This project is archived; grants have no effect until it is restored.</Alert>
          )}
          <div className="stack sm">
            {AI_CAPABILITIES.map((c) => (
              <Checkbox
                key={c}
                label={AI_CAPABILITY_LABELS[c]}
                checked={current.includes(c)}
                onChange={(on) => setCaps(on ? [...current, c] : current.filter((x) => x !== c))}
              />
            ))}
          </div>
          <ErrorAlert error={save.error} />
          <div>
            <Button onClick={() => save.mutate(undefined)} loading={save.isPending} disabled={caps === null}>
              Save grants
            </Button>
          </div>
        </>
      )}
    </Card>
  );
}

function IssueCredential({ serviceId, onClose }: { serviceId: string; onClose: () => void }) {
  const [label, setLabel] = useState('');
  const [days, setDays] = useState(90);
  const [copied, setCopied] = useState<string | null>(null);
  const issue = useAction(
    () =>
      api.post<{ clientId: string; clientSecret: string; expiresAt: string }>(
        `/admin/ai-services/${serviceId}/credentials`,
        { label, expiresInDays: days },
      ),
    { invalidate: [['ai-service', serviceId], ['ai-services']] },
  );
  const copy = (k: string, v: string) => navigator.clipboard.writeText(v).then(() => setCopied(k));
  if (issue.data) {
    return (
      <Modal
        title="Credential issued"
        onClose={onClose}
        dismissible={false}
        actions={<Button onClick={onClose}>I've saved these — dismiss</Button>}
      >
        <div className="secret-panel" role="alert">
          <span className="strong">Copy now — this secret will not be shown again.</span>
          <span className="small">Client ID</span>
          <div className="code">{issue.data.clientId}</div>
          <span className="small">Client secret</span>
          <div className="code">{issue.data.clientSecret}</div>
          <div className="row">
            <Button
              size="sm"
              variant="secondary"
              icon={<Copy size={14} />}
              onClick={() => copy('id', issue.data!.clientId)}
            >
              {copied === 'id' ? 'Copied' : 'Copy ID'}
            </Button>
            <Button
              size="sm"
              variant="secondary"
              icon={<Copy size={14} />}
              onClick={() => copy('secret', issue.data!.clientSecret)}
            >
              {copied === 'secret' ? 'Copied' : 'Copy secret'}
            </Button>
          </div>
        </div>
      </Modal>
    );
  }
  return (
    <Modal
      title="Issue credential"
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => issue.mutate(undefined)} loading={issue.isPending} disabled={!label}>
            Issue credential
          </Button>
        </>
      }
    >
      <Field label="Label" hint="Identifies where the credential is used.">
        {(p) => (
          <Input
            {...p}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. CI/CD Pipeline"
            data-autofocus
          />
        )}
      </Field>
      <Field label="Expires in (days)">
        {(p) => (
          <Input
            {...p}
            type="number"
            min={1}
            max={365}
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
          />
        )}
      </Field>
      <ErrorAlert error={issue.error} />
    </Modal>
  );
}
